import { useState, useEffect, useRef } from 'react';
import { Download } from 'lucide-react';
import { useInstallPrompt } from '../hooks/useInstallPrompt';

const C = { cream:'#faedd6', cream2:'#bcbec0', blue:'#1562c8', gold:'#f0b243', navy:'#01306d', brown:'#55381f', dark:'#2a2118', white:'#ffffff', green:'#1a8a4a', red:'#c0392b', purple:'#7c3aed' };
const BORDER = `2px solid ${C.dark}`;
const SHADOW = `6px 6px 0 ${C.dark}`;
const SHADOW_SM = `3px 3px 0 ${C.dark}`;
const FONT_PIXEL = "'GeistPixel',monospace";
const FONT_BODY = "'Roboto',sans-serif";

const MARQUEE_ITEMS = [
  { gold: true,  text: '⚡ 4 AI Tools Built-In' },
  { gold: false, text: '✦ 100% Rights-Verified Tracks' },
  { gold: false, text: '♪ AI Auto-Tagging on Every Upload' },
  { gold: true,  text: ' Split Sheet Generator' },
  { gold: false, text: ' Natural Language Catalog Search' },
  { gold: false, text: ' One-Stop Track Filter' },
  { gold: true,  text: ' AI Contract Reader' },
  { gold: false, text: ' Sync-Ready Catalog' },
  { gold: false, text: ' Direct Composer Messaging' },
];

const FAQ_ITEMS = [
  { q: "Is my music safe and private?", a: "Yes. Your tracks are stored in a private, encrypted cloud vault. Only music executives you explicitly pitch to can access them — no public browsing, no third-party sharing." },
  { q: "Who can see my uploaded tracks?", a: "Music executives who browse the Catalog can discover your songs. You control which tracks are in your portfolio and can remove them at any time." },
  { q: "How much does it cost?", a: "Coda-Vault has a free tier that's yours to keep — no credit card required. Basic and Pro plans unlock advanced features like contract analysis, unlimited uploads, and the full LegalSplits ML suite. Use code FOUNDER2026 for 6 months free on any Pro plan." },
  { q: "What makes the AI tagging different from other tools?", a: "Our AI is trained specifically on sync licensing metadata — it tags BPM, key, genre, mood, and even transcribes lyrics. The result is a pitch-ready catalog that executives can actually search by vibe." },
  { q: "What is a One-Stop track?", a: "A One-Stop track means you own 100% of both the master and the composition rights — executives can license it immediately with no split negotiations. Our platform verifies and badges these tracks." },
  { q: "How do split sheets work?", a: "Our built-in split sheet generator lets you document co-writing splits (composition & master), export them as PDF or CSV, and publish them for verification — all without leaving the platform." },
];

const PRICING_GROUPS = [
  {
    group: "🎵 For Composers",
    tiers: [
      { name: "Free", price: "Free", period: "forever", badge: null, features: ["10 song uploads per week", "Public split card visible to executives", "AI auto-tagging on every upload", "5 executive contacts / month", "3 opportunity applications / month", "Direct messaging"], cta: "Get Started Free", highlight: false },
      { name: "Basic", price: "$4.99", period: "/ month", badge: null, features: ["Unlimited song uploads", "Full contract analysis on your private splits tab", "Up to 25 exec contacts / month", "Up to 15 opportunity applications / month", "Up to 15 split sheet PDF exports / month"], cta: "Get Basic", highlight: false },
      { name: "Pro", price: "$9.99", period: "/ month", badge: "Most Popular", founderOffer: true, features: ["Unlimited song uploads", "Full contract analysis on your private splits tab", "Unlimited opportunity applications", "Unlimited split sheet PDF exports", "Deal Analyzer — upload any recording deal PDF", "Agreement Reader (co-pub, publishing admin, 360)", "Unlimited exec contacts"], cta: "Get Pro", highlight: true },
    ],
  },
  {
    group: "🏢 For Executives",
    tiers: [
      { name: "Free", price: "Free", period: "forever", badge: null, features: ["Browse full catalog", "30-second song previews", "See public split cards & verified badges", "Beta use of the Brief Helper", "5 composer contacts / month", "3 opportunity postings / month"], cta: "Get Started Free", highlight: false },
      { name: "Basic", price: "$5.99", period: "/ month", badge: null, features: ["Beta use of the Brief Helper", "Full catalog access with advanced filters", "Unlimited composer contacts", "Up to 10 opportunity postings / month", "Save up to 5 shortlists", "Contract Vault — save & manage your revisions"], cta: "Get Basic", highlight: false },
      { name: "Pro", price: "$14.99", period: "/ month", badge: "Pro", founderOffer: true, features: ["Beta use of the Brief Helper", "Full catalog access with advanced filters", "Complete song previews", "Unlimited composer contacts", "Unlimited contacts", "Unlimited postings", "Unlimited shortlists"], cta: "Get Pro", highlight: false },
    ],
  },
];

function FAQItem({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ background: open ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.03)', border: `1px solid ${open ? 'rgba(240,191,107,0.4)' : 'rgba(240,191,107,0.15)'}`, borderRadius: 10, overflow: 'hidden', transition: 'border-color 0.15s' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 22px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', gap: 16, fontFamily: FONT_BODY }}
      >
        <span style={{ color: C.white, fontSize: 15, fontWeight: 600 }}>{item.q}</span>
        <span style={{ color: 'rgba(240,191,107,0.7)', fontSize: 18, flexShrink: 0, transition: 'transform 0.2s', transform: open ? 'rotate(90deg)' : 'none' }}>›</span>
      </button>
      {open && <div style={{ padding: '0 22px 18px', color: 'rgba(255,255,255,0.55)', fontSize: 14, lineHeight: 1.75, fontFamily: FONT_BODY }}>{item.a}</div>}
    </div>
  );
}

const NAV_LINKS = [
  { label: 'For Composers', id: 'for-composers' },
  { label: 'For Supervisors', id: 'for-supervisors' },
  { label: 'How it works', id: 'how-it-works' },
  { label: 'Pricing', id: 'pricing' },
  { label: 'FAQ', id: 'faq' },
];

function scrollTo(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function useScrollReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('visible'); observer.unobserve(e.target); } }),
      { threshold: 0.12 }
    );
    document.querySelectorAll('.reveal, .reveal-left, .reveal-right').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}



/* ── Pixel window frame used for section mockups ─────────────────────────── */
function PixelWindow({ title = 'coda-vault.io', bg = C.cream, children, style = {} }) {
  return (
    <div style={{ border: BORDER, boxShadow: SHADOW, borderRadius: 12, overflow: 'hidden', background: bg, ...style }}>
      <div style={{ height: 32, background: C.blue, display: 'flex', alignItems: 'center', padding: '0 12px', gap: 7, flexShrink: 0 }}>
        {['#ff5f57','#ffbd2e','#28c840'].map((c,i) => <div key={i} style={{ width: 11, height: 11, borderRadius: '50%', background: c, border: '1px solid rgba(0,0,0,0.2)' }} />)}
        <span style={{ marginLeft: 'auto', fontFamily: FONT_PIXEL, fontSize: 12, color: 'rgba(255,255,255,0.7)', letterSpacing: '0.5px' }}>{title}</span>
      </div>
      <div style={{ padding: 20 }}>{children}</div>
    </div>
  );
}

/* ── App Showcase ────────────────────────────────────────────────────────── */
function AppShowcase({ isMobile }) {
  const { pos: twPos, done: twDone, ref: twRef } = useTypewriter("Everything in one place", 65, 300);
  const BAR = (pct, color = C.blue) => (
    <div style={{ background: 'rgba(42,33,24,0.08)', borderRadius: 4, height: 8, overflow: 'hidden' }}>
      <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4 }} />
    </div>
  );
  const Tag = ({ label, checked }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: checked ? `rgba(21,98,200,0.1)` : C.cream2, color: checked ? C.blue : C.brown, border: `1px solid ${checked ? 'rgba(21,98,200,0.3)' : 'rgba(42,33,24,0.15)'}`, borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600, fontFamily: FONT_BODY }}>
      {checked && <span style={{ color: C.green }}>✓</span>} {label}
    </span>
  );

  const screens = [
    {
      title: 'opportunities.io',
      label: 'Browse Briefs',
      content: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ background: `rgba(21,98,200,0.08)`, border: `1px solid rgba(21,98,200,0.2)`, borderRadius: 8, padding: '8px 12px', fontSize: 14, fontWeight: 700, color: C.blue, fontFamily: FONT_BODY }}>
            ⚡ 5 briefs match your style this week
          </div>
          {[
            { title: 'Emotional Underscore', sub: 'Feature Film · Apex Sound · $1,500–$3,000', tags: ['Cinematic','Piano','⚡ Match'] },
            { title: 'Latin Pop · TV Commercial', sub: 'Meridian Ent. · Deadline in 3 days', tags: ['Latin','⚡ Match'] },
          ].map((b, i) => (
            <div key={i} style={{ background: C.cream, border: BORDER, borderRadius: 8, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: C.dark, fontFamily: FONT_PIXEL }}>{b.title}</div>
              <div style={{ fontSize: 12, color: C.brown, fontFamily: FONT_BODY }}>{b.sub}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                {b.tags.map(t => <Tag key={t} label={t} checked={t.includes('Match')} />)}
                <button style={{ marginLeft: 'auto', background: C.blue, color: C.white, border: 'none', borderRadius: 5, padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: FONT_PIXEL, boxShadow: `2px 2px 0 ${C.navy}` }}>Apply</button>
              </div>
            </div>
          ))}
        </div>
      ),
    },
    {
      title: 'pitch-helper.io',
      label: 'Pitch in Minutes',
      content: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ background: C.cream, border: BORDER, borderRadius: 8, padding: '8px 12px' }}>
            <div style={{ fontSize: 14, color: C.brown, fontFamily: FONT_BODY, marginBottom: 2 }}>Brief</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.dark, fontFamily: FONT_PIXEL }}>Emotional Underscore — Feature Film</div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {[{ icon: '🎯', label: 'Pro', active: true }, { icon: '💬', label: 'Personal' }, { icon: '📖', label: 'Story' }].map(t => (
              <button key={t.label} style={{ flex: 1, background: t.active ? C.dark : C.cream, color: t.active ? C.white : C.brown, border: BORDER, borderRadius: 6, padding: '6px 4px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: FONT_BODY }}>
                {t.icon} {t.label}
              </button>
            ))}
          </div>
          <div style={{ background: C.cream, border: `1px solid rgba(42,33,24,0.15)`, borderRadius: 8, padding: '10px 12px', fontSize: 12, color: C.dark, fontFamily: FONT_BODY, lineHeight: 1.5 }}>
            "My track <em>Dusk & Silence</em> layers sparse piano against evolving strings — designed for emotional restraint that builds to release, fitting your brief's narrative arc..."
          </div>
          <div style={{ background: C.cream2, border: BORDER, borderRadius: 8, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <img src="/icons/page.png" alt="" style={{ width: 16, height: 16, imageRendering: 'pixelated' }} />
            <div style={{ fontSize: 12, color: C.dark, fontFamily: FONT_BODY, flex: 1 }}>
              <span style={{ fontWeight: 700 }}>Dusk & Silence</span> · Piano · Cinematic · 3:42 · D Minor
            </div>
          </div>
          <button style={{ background: C.blue, color: C.white, border: BORDER, borderRadius: 6, padding: '8px 0', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: FONT_PIXEL, boxShadow: `3px 3px 0 ${C.navy}`, width: '100%' }}>
            Submit Application
          </button>
        </div>
      ),
    },
    {
      title: 'ai-analysis.io',
      label: 'AI Analysis',
      content: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ background: `rgba(26,138,74,0.08)`, border: `1px solid rgba(26,138,74,0.25)`, borderRadius: 8, padding: '8px 12px', fontSize: 14, fontWeight: 700, color: C.green, fontFamily: FONT_BODY }}>
            ✅ Analysis complete — 97% confidence
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
            {[{ v: '72', l: 'BPM' }, { v: 'D Min', l: 'Key' }, { v: '4/4', l: 'Time' }].map(s => (
              <div key={s.l} style={{ background: C.cream, border: BORDER, borderRadius: 8, padding: '8px 6px', textAlign: 'center' }}>
                <div style={{ fontFamily: FONT_PIXEL, fontSize: 14, color: C.dark, fontWeight: 700 }}>{s.v}</div>
                <div style={{ fontFamily: FONT_BODY, fontSize: 12, color: C.brown }}>{s.l}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {[{ label: 'Cinematic', pct: 94 }, { label: 'Emotional', pct: 89 }, { label: 'Ambient', pct: 72 }].map(g => (
              <div key={g.label}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: C.dark, fontFamily: FONT_BODY }}>{g.label}</span>
                  <span style={{ fontSize: 12, color: C.brown, fontFamily: FONT_BODY }}>{g.pct}%</span>
                </div>
                {BAR(g.pct)}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {[['Piano',true],['Strings',true],['Film',false],['TV Drama',false],['Documentary',false]].map(([t, c]) => (
              <Tag key={t} label={t} checked={c} />
            ))}
          </div>
        </div>
      ),
    },
  ];

  return (
    <div id="app-showcase" style={{ background: C.cream, borderTop: BORDER, padding: isMobile ? '60px 20px' : '80px 48px' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div className="reveal" style={{ textAlign: 'center', marginBottom: 48 }}>
          <div style={{ display: 'inline-block', fontSize: 14, padding: '4px 12px', color: C.blue, marginBottom: 16, letterSpacing: '1.5px', fontWeight: 800, fontFamily: FONT_BODY }}>BUILT FOR HOW SYNC WORKS</div>
          <h2 ref={twRef} style={{ fontFamily: FONT_PIXEL, fontSize: isMobile ? 26 : 40, color: C.dark, margin: 0 }}>
            {renderTyped("Everything in one place", twPos, twDone)}
          </h2>
        </div>
        <div className="reveal" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 24 }}>
          {screens.map(s => (
            <div key={s.title} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontFamily: FONT_BODY, fontSize: 14, color: C.dark, textAlign: 'center' }}>{s.label}</div>
              <PixelWindow title={s.title} bg={C.cream}>
                {s.content}
              </PixelWindow>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Typewriter hook ─────────────────────────────────────────────────────── */
function useTypewriter(text, speed = 62, startDelay = 0) {
  const [pos, setPos] = useState(0);
  const [active, setActive] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setActive(true); obs.disconnect(); } },
      { threshold: 0.15 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  useEffect(() => {
    if (!active || pos >= text.length) return;
    const t = setTimeout(() => setPos(p => p + 1), pos === 0 ? startDelay : speed);
    return () => clearTimeout(t);
  }, [active, pos, text, speed, startDelay]);
  return { pos, done: pos >= text.length, ref };
}

/* ── Shared typewriter renderer ──────────────────────────────────────────── */
function renderTyped(text, pos, done, { accentColor, plainAfterNl = 0 } = {}) {
  const shown = text.slice(0, pos);
  const nlAt = text.indexOf('\n');
  if (nlAt === -1) {
    return (
      <>
        {shown}
        {!done && <span className="tw-cursor" style={{ color: accentColor || 'currentColor' }}>|</span>}
      </>
    );
  }
  const line1 = shown.slice(0, Math.min(shown.length, nlAt));
  const hasNl = shown.length > nlAt;
  const rest = hasNl ? shown.slice(nlAt + 1) : '';
  const plain2 = rest.slice(0, Math.min(rest.length, plainAfterNl));
  const colorable = rest.length > plainAfterNl ? rest.slice(plainAfterNl) : '';
  return (
    <>
      {line1}
      {hasNl && <br />}
      {plain2}
      {colorable && accentColor ? <span style={{ color: accentColor }}>{colorable}</span> : colorable}
      {!done && <span className="tw-cursor" style={{ color: accentColor || 'currentColor' }}>|</span>}
    </>
  );
}

/* ── Dual Value Props ────────────────────────────────────────────────────── */
function DualPropsSection({ isMobile, onGetStarted }) {
  const COMPOSER_TEXT = "From pitch\nto payment.";
  const SUPERVISOR_TEXT = "From brief\nto placement.";
  const { pos: cPos, done: cDone, ref: cRef } = useTypewriter(COMPOSER_TEXT, 62, 200);
  const { pos: sPos, done: sDone, ref: sRef } = useTypewriter(SUPERVISOR_TEXT, 62, 350);

  const FS = isMobile ? 46 : 70;
  const h2Style = { fontFamily: FONT_PIXEL, fontSize: FS, color: C.dark, lineHeight: 1.05, letterSpacing: '-2.5px', marginBottom: 28, minHeight: FS * 2.25 };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', background: C.cream, borderTop: BORDER }}>
      <div id="for-composers" ref={cRef} style={{ padding: isMobile ? '52px 20px 64px' : '72px 56px 88px', borderRight: isMobile ? 'none' : `1px solid rgba(0, 0, 0, 0.15)`, borderBottom: isMobile ? `1px solid rgba(42,33,24,0.15)` : 'none' }}>
        <span style={{ fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 700, color: 'rgba(0, 0, 0, 0.97)', marginBottom: 28, display: 'block', fontFamily: FONT_BODY }}>For composers</span>
        <h2 style={h2Style}>{renderTyped(COMPOSER_TEXT, cPos, cDone, { accentColor: C.gold, plainAfterNl: 3 })}</h2>
        <p style={{ fontSize: 14, color: C.brown, lineHeight: 1.8, marginBottom: 36, maxWidth: 300, fontFamily: FONT_BODY }}>
          You make the music. We handle the pitch, the rights, and the deal — so you collect royalties instead of chasing emails.
        </p>
        <button onClick={onGetStarted}
          style={{ fontFamily: FONT_BODY, fontSize: 13, fontWeight: 500, color: C.dark, background: C.gold, border: BORDER, borderRadius: 6, padding: '9px 22px', cursor: 'pointer', boxShadow: SHADOW_SM, transition: 'box-shadow 0.1s, transform 0.1s' }}
          onMouseEnter={e => { e.currentTarget.style.boxShadow = `1px 1px 0 ${C.dark}`; e.currentTarget.style.transform = 'translate(2px,2px)'; }}
          onMouseLeave={e => { e.currentTarget.style.boxShadow = SHADOW_SM; e.currentTarget.style.transform = 'translate(0,0)'; }}
        >Start as a composer</button>
      </div>
      <div id="for-supervisors" ref={sRef} style={{ padding: isMobile ? '52px 20px 64px' : '72px 56px 88px' }}>
        <span style={{ fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 700, color: 'rgb(0, 0, 0)', marginBottom: 28, display: 'block', fontFamily: FONT_BODY }}>For supervisors</span>
        <h2 style={h2Style}>{renderTyped(SUPERVISOR_TEXT, sPos, sDone, { accentColor: C.navy, plainAfterNl: 3 })}</h2>
        <p style={{ fontSize: 15, color: C.brown, lineHeight: 1.8, marginBottom: 36, maxWidth: 300, fontFamily: FONT_BODY }}>
          Post a brief, get matched pitches from verified composers, close the deal. No inbox chaos. No uncleared rights.
        </p>
        <button onClick={onGetStarted}
          style={{ fontFamily: FONT_BODY, fontSize: 13, fontWeight: 500, color: C.dark, background: C.gold, border: BORDER, borderRadius: 6, padding: '9px 22px', cursor: 'pointer', boxShadow: SHADOW_SM, transition: 'box-shadow 0.1s, transform 0.1s' }}
          onMouseEnter={e => { e.currentTarget.style.boxShadow = `1px 1px 0 ${C.dark}`; e.currentTarget.style.transform = 'translate(2px,2px)'; }}
          onMouseLeave={e => { e.currentTarget.style.boxShadow = SHADOW_SM; e.currentTarget.style.transform = 'translate(0,0)'; }}
        >Post a brief</button>
      </div>
    </div>
  );
}

/* ── Main LandingPage component ──────────────────────────────────────────── */
export function LandingPage({ onGetStarted, onLegalPage }) {
  const [activeSection, setActiveSection] = useState('');

  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const { canInstall, install } = useInstallPrompt();
  const { pos: howPos, done: howDone, ref: howRef } = useTypewriter("Three steps.\nOne deal.", 65, 200);
  const { pos: ctaPos, done: ctaDone, ref: ctaRef } = useTypewriter("Ready to get placed?", 65, 200);

  useScrollReveal();

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => { entries.forEach(e => { if (e.isIntersecting) setActiveSection(e.target.id); }); },
      { threshold: 0.3 }
    );
    NAV_LINKS.forEach(({ id }) => { const el = document.getElementById(id); if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div style={{ minHeight: '100vh', fontFamily: FONT_BODY, color: C.dark, overflowX: 'hidden', background: C.cream }}>

      {/* ── Scanline texture ───────────────────────────────────────────── */}
      <div style={{ position: 'fixed', inset: 0, background: 'repeating-linear-gradient(0deg,transparent,transparent 3px,rgba(42,33,24,0.012) 3px,rgba(42,33,24,0.012) 4px)', pointerEvents: 'none', zIndex: 9999 }} />

      {/* ── Nav ─────────────────────────────────────────────────────────── */}
      <div style={{ height: 'calc(64px + env(safe-area-inset-top, 0px))', flexShrink: 0 }} />
      <nav style={{ padding: isMobile ? '0 20px' : '0 48px', paddingTop: 'env(safe-area-inset-top, 0px)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: scrolled ? BORDER : '2px solid transparent', position: 'fixed', top: 0, left: 0, right: 0, height: 'calc(64px + env(safe-area-inset-top, 0px))', background: C.cream, transition: 'border-color 0.2s', zIndex: 1000 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <img src="/cv-logo.svg" alt="Coda-Vault" style={{ width: 32, height: 32, objectFit: 'contain', borderRadius: 7 }} onError={(e) => e.target.style.display = 'none'} />
          <div style={{ fontSize: isMobile ? 18 : 26, fontWeight: 800, fontFamily: FONT_PIXEL, color: C.dark, letterSpacing: '-0.3px' }}>Coda-Vault</div>
        </div>
        {!isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {NAV_LINKS.map(({ label, id }) => (
              <button key={id} onClick={() => scrollTo(id)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 500, color: activeSection === id ? C.blue : C.dark, fontFamily: FONT_BODY, transition: 'color 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.color = C.blue; }}
                onMouseLeave={e => { e.currentTarget.style.color = activeSection === id ? C.blue : C.dark; }}
              >{label}</button>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          {!isMobile && (
            <div style={{ background: C.gold, border: BORDER, borderRadius: 6, padding: '9px 22px', color: C.dark, fontSize: 12, fontWeight: 500, letterSpacing: '0.6px', fontFamily: FONT_BODY, boxShadow: SHADOW_SM }}>EARLY ACCESS</div>
          )}
          <button onClick={onGetStarted}
            style={{ background: C.gold, color: C.dark, border: BORDER, borderRadius: 6, padding: isMobile ? '8px 16px' : '9px 22px', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: FONT_BODY, boxShadow: SHADOW_SM, transition: 'box-shadow 0.1s, transform 0.1s' }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = `1px 1px 0 ${C.dark}`; e.currentTarget.style.transform = 'translate(2px,2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = SHADOW_SM; e.currentTarget.style.transform = 'translate(0,0)'; }}
          >{isMobile ? 'Join Free' : 'Sign In / Sign Up'}</button>
          {isMobile && (
            <button onClick={() => setMobileNavOpen(o => !o)} style={{ background: C.white, border: BORDER, borderRadius: 6, width: 36, height: 36, cursor: 'pointer', color: C.dark, fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '1px 1px 0 rgba(42,33,24,0.2)' }} aria-label="Toggle menu">
              {mobileNavOpen ? '✕' : '☰'}
            </button>
          )}
        </div>
      </nav>

      {isMobile && mobileNavOpen && (
        <div style={{ position: 'fixed', top: 'calc(64px + env(safe-area-inset-top, 0px))', left: 0, right: 0, background: C.cream, borderBottom: BORDER, zIndex: 999, display: 'flex', flexDirection: 'column', padding: '12px 0 20px', boxShadow: `0 8px 0 rgba(42,33,24,0.2)` }}>
          {NAV_LINKS.map(({ label, id }) => (
            <button key={id} onClick={() => { scrollTo(id); setMobileNavOpen(false); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '14px 24px', textAlign: 'left', fontSize: 16, fontWeight: 600, color: activeSection === id ? C.blue : C.dark, fontFamily: FONT_BODY }}
            >{label}</button>
          ))}
        </div>
      )}

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <div style={{ background: '#1a1208', padding: isMobile ? '56px 20px 80px' : '80px 48px 100px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <h1 style={{ fontSize: isMobile ? 48 : 84, fontWeight: 800, marginBottom: 24, lineHeight: 1.0, fontFamily: FONT_PIXEL, color: C.white, letterSpacing: '-2px' }}>
            <span className="word-reveal" style={{ animationDelay: '0s' }}>Get</span>{' '}
            <span className="word-reveal" style={{ animationDelay: '0.11s' }}>your</span>{' '}
            <span className="word-reveal" style={{ animationDelay: '0.22s' }}>music</span>{' '}
            <br />
            <span className="word-reveal" style={{ color: C.gold, animationDelay: '0.35s' }}>placed.</span>
          </h1>

          <p className="word-reveal" style={{ fontSize: isMobile ? 16 : 22, color: '#faedd6', maxWidth: isMobile ? '100%' : 520, margin: '0 auto 40px', lineHeight: 1.3, fontFamily: FONT_BODY, animationDelay: '0.52s' }}>
            Upload your track. Our AI writes the pitch, verifies your rights, and puts you in front of the supervisors who need exactly your sound.
          </p>

          <div className="reveal reveal-delay-1" style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'center', alignItems: isMobile ? 'stretch' : 'center', gap: 14, flexWrap: 'wrap' }}>
            <button onClick={onGetStarted}
              style={{ background: C.gold, color: C.dark, border: BORDER, borderRadius: 8, padding: '15px 36px', fontSize: 16, fontWeight: 500, cursor: 'pointer', fontFamily: FONT_BODY, boxShadow: SHADOW, transition: 'box-shadow 0.1s, transform 0.1s', width: isMobile ? '100%' : 'auto', boxSizing: 'border-box' }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = `2px 2px 0 ${C.dark}`; e.currentTarget.style.transform = 'translate(4px,4px)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = SHADOW; e.currentTarget.style.transform = 'translate(0,0)'; }}
            >Get Started Free</button>
            <a href="#app-showcase" onClick={(e) => { e.preventDefault(); document.getElementById('app-showcase')?.scrollIntoView({ behavior: 'smooth' }); }}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#bcbec0', color: C.dark, border: BORDER, borderRadius: 8, padding: '15px 28px', fontSize: 15, fontWeight: 500, cursor: 'pointer', fontFamily: FONT_BODY, textDecoration: 'none', transition: 'background 0.15s', width: isMobile ? '100%' : 'auto', boxSizing: 'border-box' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#a8aaac'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#bcbec0'; }}
            >See it in Action</a>
            {canInstall && (
              <button onClick={install}
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: 'rgba(255,255,255,0.1)', color: C.white, border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, padding: '15px 28px', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: FONT_PIXEL, transition: 'background 0.15s', width: isMobile ? '100%' : 'auto', boxSizing: 'border-box' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.16)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
              ><Download size={14} /> Download the App</button>
            )}
          </div>

        </div>
      </div>

      {/* ── Marquee strip ───────────────────────────────────────────────── */}
      <div style={{ background: C.dark, borderTop: BORDER, borderBottom: BORDER, overflow: 'hidden', padding: '13px 0' }}>
        <div className="marquee-track">
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', padding: '0 24px', whiteSpace: 'nowrap', fontFamily: FONT_PIXEL, fontSize: 12, color: item.gold ? C.gold : 'rgba(255,255,255,0.42)', letterSpacing: '0.04em', gap: 20 }}>
              {item.text}
              <span style={{ color: 'rgba(255,255,255,0.14)', fontSize: 12 }}>◆</span>
            </span>
          ))}
        </div>
      </div>

      {/* ── Dual Value Props ────────────────────────────────────────────── */}
      <DualPropsSection isMobile={isMobile} onGetStarted={onGetStarted} />

      {/* ── How It Works ────────────────────────────────────────────────── */}
      <div id="how-it-works" style={{ background: '#1a1208', borderTop: BORDER, padding: isMobile ? '72px 20px 88px' : '96px 48px 112px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div className="reveal" style={{ marginBottom: isMobile ? 52 : 72 }}>
            <span style={{ fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 700, color: 'rgba(220, 159, 53, 0.99)', marginBottom: 20, display: 'block', fontFamily: FONT_BODY }}>How it works</span>
            <h2 ref={howRef} style={{ fontFamily: FONT_PIXEL, fontSize: isMobile ? 38 : 58, color: C.white, lineHeight: 1.0, letterSpacing: '-2px', margin: 0, minHeight: isMobile ? 82 : 122 }}>
              {renderTyped("Three steps.\nOne deal.", howPos, howDone, { accentColor: C.gold })}
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)' }}>
            {[
              { num: '01', title: 'Live in 60 seconds', desc: 'Drop your track. AI tags genre, mood, BPM, key, and instruments automatically. Your catalog is pitch-ready the moment you hit save.', icon: '/icons/page.png' },
              { num: '02', title: 'Apply in minutes', desc: 'Browse open briefs and pitch with one click. Our AI writes a tailored pitch letter for each supervisor based on your track and their brief.', icon: '/icons/folder.png' },
              { num: '03', title: 'From pitch to payment', desc: 'Split sheets verified. Contract analyzed. Deal closed over direct message — without ever leaving the platform.', icon: '/icons/brief folder.png' },
            ].map(({ num, title, desc, icon }, i) => (
              <div key={num} className={`reveal${i > 0 ? ` reveal-delay-${i}` : ''}`}
                style={{ padding: isMobile ? '40px 0' : `0 ${i === 0 ? '0' : '48px'} 0 ${i > 0 ? '48px' : '0'}`, borderLeft: !isMobile && i > 0 ? `1px solid rgba(255,255,255,0.08)` : 'none', borderTop: isMobile && i > 0 ? `1px solid rgba(255,255,255,0.08)` : 'none' }}>
                <div style={{ fontFamily: FONT_PIXEL, fontSize: 56, color: 'rgba(240, 178, 67, 1)', lineHeight: 1, marginBottom: 24 }}>{num}</div>
                <div style={{ fontFamily: FONT_BODY, fontSize: 18, color: C.white, marginBottom: 14, lineHeight: 1.25 }}>{title}</div>
                <p style={{ fontSize: 16, color: C.white, lineHeight: 1.78, margin: 0, fontFamily: FONT_BODY }}>{desc}</p>
              </div>
            ))}
          </div>
          <div style={{ marginTop: isMobile ? 56 : 80, paddingTop: isMobile ? 48 : 64, borderTop: `1px solid rgba(255,255,255,0.07)`, textAlign: 'center' }}>
            <button onClick={onGetStarted}
              style={{ background: C.gold, color: C.dark, border: BORDER, borderRadius: 8, padding: '14px 40px', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: FONT_PIXEL, boxShadow: SHADOW_SM, transition: 'box-shadow 0.1s, transform 0.1s' }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = `1px 1px 0 ${C.dark}`; e.currentTarget.style.transform = 'translate(2px,2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = SHADOW_SM; e.currentTarget.style.transform = 'translate(0,0)'; }}
            >Get Started Free </button>
          </div>
        </div>
      </div>

      {/* ── App Showcase ─────────────────────────────────────────────────── */}
      <AppShowcase isMobile={isMobile} />

      {/* ── Pricing ─────────────────────────────────────────────────────── */}
      <div id="pricing" style={{ background: C.cream, borderTop: BORDER, padding: isMobile ? '60px 20px 80px' : '80px 48px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div className="reveal" style={{ textAlign: 'center', marginBottom: 52 }}>
            <div style={{ display: 'inline-block', fontSize: 14, padding: '4px 12px', borderRadius: 20, color: '#040404', marginBottom: 18, letterSpacing: '1.8px', fontWeight: 800, fontFamily: FONT_BODY }}>PRICING</div>
            <h2 style={{ fontFamily: FONT_PIXEL, fontSize: isMobile ? 28 : 44, color: C.dark, margin: '0 0 10px' }}>
              Simple, Transparent Pricing
            </h2>
            <p style={{ fontSize: 16, color: C.brown, margin: 0, fontFamily: FONT_BODY }}>
              Start free. Upgrade when you're ready.{' '}
              Use code <strong style={{ color: C.dark, fontFamily: FONT_PIXEL }}>FOUNDER2026</strong> for 6 months free Pro.
            </p>
          </div>

          {PRICING_GROUPS.map((group, gi) => (
            <div key={group.group} style={{ marginBottom: gi === 0 ? 56 : 0 }}>
              <div style={{ marginBottom: 20, paddingBottom: 12, borderBottom: `1px solid rgba(42,33,24,0.15)` }}>
                <h3 style={{ fontSize: 16, fontWeight: 800, color: C.dark, fontFamily: FONT_PIXEL, margin: 0 }}>{group.group}</h3>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 20, alignItems: 'start' }}>
                {group.tiers.map((tier, ti) => (
                  <div key={tier.name} className={`reveal reveal-delay-${ti + 1}`} style={{ background: tier.highlight ? 'rgba(21,98,200,0.06)' : C.white, border: tier.highlight ? `2px solid ${C.blue}` : BORDER, borderRadius: 12, padding: isMobile ? '24px 20px' : '28px 24px', position: 'relative', boxShadow: tier.highlight ? `0 0 0 2px ${C.blue}, 3px 3px 0 ${C.dark}` : SHADOW_SM }}>
                    {tier.badge && (
                      <div style={{ position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)', background: C.blue, color: C.white, fontSize: 12, fontWeight: 800, padding: '4px 14px', borderRadius: 20, letterSpacing: '0.5px', whiteSpace: 'nowrap', border: BORDER, fontFamily: FONT_PIXEL }}>{tier.badge}</div>
                    )}
                    <div style={{ marginBottom: 16 }}>
                      <h4 style={{ fontSize: 14, fontWeight: 800, marginBottom: 8, color: C.dark, fontFamily: FONT_PIXEL }}>{tier.name}</h4>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                        <span style={{ fontSize: 32, fontWeight: 900, color: tier.highlight ? C.blue : C.dark, fontFamily: FONT_PIXEL }}>{tier.price}</span>
                        {tier.period && <span style={{ fontSize: 12, color: C.brown, fontFamily: FONT_BODY }}>{tier.period}</span>}
                      </div>
                    </div>
                    {tier.founderOffer && (
                      <div style={{ background: 'rgba(240,191,107,0.12)', border: `1px solid rgba(240,191,107,0.35)`, borderRadius: 8, padding: '10px 12px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ flexShrink: 0, fontSize: 13 }}>★</span>
                        <div>
                          <div style={{ color: '#a07020', fontSize: 12, fontWeight: 800, fontFamily: FONT_PIXEL }}>🎉 FOUNDER OFFER</div>
                          <div style={{ color: C.brown, fontSize: 12, marginTop: 2, fontFamily: FONT_BODY }}>Use code <span style={{ fontFamily: FONT_PIXEL, fontWeight: 700, color: C.dark }}>FOUNDER2026</span> for 6 months free</div>
                        </div>
                      </div>
                    )}
                    <ul style={{ listStyle: 'none', margin: '0 0 24px', padding: 0, display: 'flex', flexDirection: 'column', gap: 9 }}>
                      {tier.features.map(f => (
                        <li key={f} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                          <span style={{ color: C.green, fontSize: 13, flexShrink: 0, marginTop: 1, fontWeight: 700 }}>✓</span>
                          <span style={{ fontSize: 13, color: C.dark, lineHeight: 1.5, fontFamily: FONT_BODY }}>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <button onClick={onGetStarted}
                      style={{ width: '100%', background: tier.highlight ? C.blue : C.dark, color: C.white, border: BORDER, borderRadius: 8, padding: '11px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: FONT_PIXEL, boxShadow: SHADOW_SM, transition: 'box-shadow 0.1s, transform 0.1s' }}
                      onMouseEnter={e => { e.currentTarget.style.boxShadow = '1px 1px 0 rgba(42,33,24,0.3)'; e.currentTarget.style.transform = 'translate(1px,1px)'; }}
                      onMouseLeave={e => { e.currentTarget.style.boxShadow = SHADOW_SM; e.currentTarget.style.transform = 'translate(0,0)'; }}
                    >{tier.cta}</button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── FAQ ─────────────────────────────────────────────────────────── */}
      <div id="faq" style={{ background: C.dark, borderTop: BORDER, padding: isMobile ? '60px 20px 80px' : '80px 48px' }}>
        <div style={{ maxWidth: 740, margin: '0 auto' }}>
          <div className="reveal" style={{ textAlign: 'center', marginBottom: 48 }}>
            <div style={{ display: 'inline-block', fontSize: 12, padding: '4px 12px', borderRadius: 20, background: 'rgba(240,191,107,0.15)', color: C.gold, border: `1px solid rgba(240,191,107,0.3)`, marginBottom: 18, letterSpacing: '1.8px', fontWeight: 800, fontFamily: FONT_BODY }}>FAQ</div>
            <h2 style={{ fontFamily: FONT_PIXEL, fontSize: isMobile ? 26 : 32, color: C.white, margin: 0 }}>Frequently Asked Questions</h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {FAQ_ITEMS.map(item => <FAQItem key={item.q} item={item} />)}
          </div>
        </div>
      </div>

      {/* ── Final CTA ───────────────────────────────────────────────────── */}
      <div style={{ background: C.blue, borderTop: BORDER, padding: isMobile ? '60px 20px 80px' : '80px 48px', position: 'relative', overflow: 'hidden' }}>
        {/* Decorative page icons scattered */}
        <img src="/icons/page.png" alt="" style={{ position: 'absolute', top: 30, left: isMobile ? 10 : 80, width: 48, imageRendering: 'pixelated', opacity: 0.15, animation: 'floatA 5s ease-in-out infinite' }} />
        <img src="/icons/page.png" alt="" style={{ position: 'absolute', top: 60, right: isMobile ? 10 : 100, width: 36, imageRendering: 'pixelated', opacity: 0.1, animation: 'floatB 4.5s ease-in-out 1s infinite' }} />
        <img src="/icons/page.png" alt="" style={{ position: 'absolute', bottom: 40, left: isMobile ? 20 : 200, width: 28, imageRendering: 'pixelated', opacity: 0.1, animation: 'floatA 6s ease-in-out 2s infinite' }} />
        <img src="/icons/disc.png" alt="" style={{ position: 'absolute', bottom: 30, right: isMobile ? 20 : 160, width: 44, imageRendering: 'pixelated', opacity: 0.12, animation: 'spin 16s linear infinite' }} />

        <div style={{ maxWidth: 640, margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'inline-block', fontSize: 12, padding: '4px 12px', borderRadius: 20, background: 'rgba(240,191,107,0.2)', color: C.gold, border: `1px solid rgba(240,191,107,0.35)`, marginBottom: 24, letterSpacing: '1.8px', fontWeight: 800, fontFamily: FONT_BODY }}>FOUNDING MEMBER ACCESS</div>
          <h2 ref={ctaRef} style={{ fontFamily: FONT_PIXEL, fontSize: isMobile ? 34 : 52, color: C.white, margin: '0 0 16px', lineHeight: 1.05, letterSpacing: '-1.5px' }}>
            {renderTyped("Ready to get placed?", ctaPos, ctaDone)}
          </h2>
          <p style={{ fontSize: isMobile ? 15 : 17, color: 'rgba(255,255,255,0.6)', maxWidth: 400, margin: '0 auto 40px', lineHeight: 1.75, fontFamily: FONT_BODY }}>
            Your next sync deal starts here. Join free — no credit card, no waiting list.
          </p>
          <button onClick={onGetStarted}
            style={{ background: C.gold, color: C.dark, border: BORDER, borderRadius: 8, padding: isMobile ? '16px 32px' : '18px 52px', fontSize: isMobile ? 15 : 17, fontWeight: 700, cursor: 'pointer', fontFamily: FONT_PIXEL, boxShadow: `5px 5px 0 ${C.dark}`, transition: 'box-shadow 0.1s, transform 0.1s', width: isMobile ? '100%' : 'auto' }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = `2px 2px 0 ${C.dark}`; e.currentTarget.style.transform = 'translate(3px,3px)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = `5px 5px 0 ${C.dark}`; e.currentTarget.style.transform = 'translate(0,0)'; }}
          >Get Started Free →</button>
        </div>
      </div>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <div style={{ borderTop: BORDER, background: C.dark, padding: isMobile ? '32px 20px' : '40px 48px', textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 12 }}>
          <img src="/cv-logo.svg" alt="Coda-Vault" style={{ width: 24, height: 24, objectFit: 'contain', borderRadius: 5 }} onError={(e) => e.target.style.display = 'none'} />
          <span style={{ fontFamily: FONT_PIXEL, fontSize: 15, color: C.white }}>Coda-Vault</span>
        </div>
        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: isMobile ? 13 : 14, marginBottom: 8, fontFamily: FONT_BODY }}>Questions? We'd love to hear from you.</p>
        <a href="mailto:hello@coda-vault.com"
          style={{ color: C.gold, fontSize: isMobile ? 14 : 15, fontWeight: 600, textDecoration: 'none', fontFamily: FONT_BODY }}
          onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
          onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
        >hello@coda-vault.com</a>
        <div style={{ marginTop: 20, display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'center', alignItems: 'center', gap: isMobile ? 12 : 24, flexWrap: 'wrap' }}>
          {[{ label: 'Terms of Service', page: 'terms' }, { label: 'Privacy Policy', page: 'privacy' }, { label: 'DMCA Policy', page: 'dmca' }].map(({ label, page }) => (
            <a key={page} href={`#${page}`} onClick={(e) => { e.preventDefault(); onLegalPage(page); }}
              style={{ color: 'rgba(255,255,255,0.35)', fontSize: isMobile ? 12 : 13, cursor: 'pointer', textDecoration: 'underline', fontFamily: FONT_BODY }}>{label}</a>
          ))}
        </div>
        <p style={{ color: 'rgba(255,255,255,0.25)', fontSize: isMobile ? 11 : 12, marginTop: 16, fontFamily: FONT_BODY }}>
          © {new Date().getFullYear()} Coda-Vault. Where talent meets opportunity.
        </p>
      </div>
    </div>
  );
}
